import React, { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { usePageHeader } from "../contexts/PageHeaderContext";
import CustomerProfileOverlay from "../components/CustomerProfileOverlay";
import RescheduleTextPopup from "../components/RescheduleTextPopup";
import AssignTechControl from "../components/AssignTechControl";
import { Button } from "../components/ui/button";
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Filter,
  Plus,
  MapPin,
  Phone,
  Clock,
  DollarSign,
  AlertCircle,
  X,
  GripVertical,
  Inbox,
  Briefcase,
  Users,
} from "lucide-react";
import { dispatchPriorityColors, getJobColor } from "../data/dispatchMock";
import { jobsApi, techniciansApi } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import LiveFleetMap from "../components/dispatch/LiveFleetMap";
import { toast } from "sonner";

const HOUR_WIDTH = 96;
const START_HOUR = 6;
const END_HOUR = 19; // exclusive end (so last column header is 18:00 = 6 PM)
const ROW_HEIGHT = 80;
const HOURS = Array.from({ length: END_HOUR - START_HOUR + 1 }, (_, i) => START_HOUR + i);

function fmtHour(h) {
  const period = h >= 12 ? "PM" : "AM";
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh} ${period}`;
}

function timeToOffset(iso) {
  const d = new Date(iso);
  const minutes = d.getHours() * 60 + d.getMinutes();
  const startMin = START_HOUR * 60;
  return ((minutes - startMin) / 60) * HOUR_WIDTH;
}

function durationMinutes(s, e) {
  return (new Date(e) - new Date(s)) / 60000;
}

function offsetToTime(px) {
  const minutes = (px / HOUR_WIDTH) * 60 + START_HOUR * 60;
  const totalSnapped = Math.round(minutes / 15) * 15;
  const h = Math.floor(totalSnapped / 60);
  const m = totalSnapped % 60;
  return { h, m };
}

function localDateStr(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function startOfWeek(d) {
  const out = new Date(d);
  const day = out.getDay(); // 0=Sun
  out.setDate(out.getDate() - day);
  out.setHours(0, 0, 0, 0);
  return out;
}

function startOfMonth(d) {
  const out = new Date(d.getFullYear(), d.getMonth(), 1);
  out.setHours(0, 0, 0, 0);
  return out;
}

function pad(n) {
  return String(n).padStart(2, "0");
}

function isoFromHM(baseIso, h, m) {
  // Keep local-time format to match how mock data stores it (YYYY-MM-DDTHH:MM)
  const datePart = baseIso.slice(0, 10);
  return `${datePart}T${pad(h)}:${pad(m)}`;
}

function shortDate(d) {
  return d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
}

// "Mon, May 25 · 1:00 PM" — used on Unassigned cards so the dispatcher
// can see the requested appointment time at a glance.
function formatScheduledLabel(iso) {
  if (!iso) return null;
  const d = new Date(iso.length === 16 ? `${iso}:00` : iso);
  if (Number.isNaN(d.getTime())) return null;
  const date = d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return `${date} · ${time}`;
}

export default function DispatchBoard() {
  const { hasModule } = useAuth();
  const [date, setDate] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [view, setView] = useState("day"); // 'day' | 'week' | 'month'
  const [jobs, setJobs] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [techs, setTechs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tradeFilter, setTradeFilter] = useState("All");
  const [selectedJob, setSelectedJob] = useState(null);
  const [profileCustomerId, setProfileCustomerId] = useState(null);
  const [draggingJob, setDraggingJob] = useState(null);
  const [dragOverRow, setDragOverRow] = useState(null);
  const [rescheduleJob, setRescheduleJob] = useState(null);
  const [now, setNow] = useState(new Date());
  const scrollRef = useRef(null);

  // Load from API
  useEffect(() => {
    let cancelled = false;
    const loadAll = () =>
      Promise.all([jobsApi.list(), techniciansApi.list()])
        .then(([allJobs, allTechs]) => {
          if (cancelled) return;
          const assigned = allJobs.filter((j) => j.status !== "Unassigned" && j.start);
          const unassignedJobs = allJobs.filter((j) => j.status === "Unassigned");
          setJobs(assigned);
          setUnassigned(unassignedJobs);
          // DispatchBoard's tech shape is simpler — adapt
          setTechs(
            allTechs.map((t) => ({
              id: t.id,
              name: t.name,
              role: t.role,
              avatar: t.avatar,
              color: t.color,
              status: t.status,
              phone: t.phone,
            })),
          );
          setLoading(false);
        })
        .catch((err) => {
          console.error(err);
          toast.error("Failed to load dispatch board data");
          setLoading(false);
        });
    loadAll();
    const onJobCreated = () => loadAll();
    window.addEventListener("job-created", onJobCreated);
    return () => {
      cancelled = true;
      window.removeEventListener("job-created", onJobCreated);
    };
  }, []);

  // Update "now" line every minute
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  const filteredTechs = useMemo(() => {
    if (tradeFilter === "All") return techs;
    const map = { HVAC: ["HVAC", "Sr HVAC"], Plumbing: ["Plumbing"], Electrical: ["Electrical"] };
    const allowed = map[tradeFilter] || [];
    return techs.filter((t) => allowed.some((a) => t.role.includes(a)));
  }, [tradeFilter, techs]);

  const todayJobs = useMemo(() => {
    const dStr = localDateStr(date);
    return jobs.filter((j) => j.start && j.start.startsWith(dStr));
  }, [jobs, date]);

  // ---- Drag & drop ----
  // `sourceTechId` = which tech's assignment (or row) the tile was dragged from.
  // For a multi-tech job this tells us which piece to move; for legacy solo
  // jobs it equals the sole techId.
  const onJobDragStart = (e, job, source = "board", sourceTechId = null) => {
    setDraggingJob({ ...job, _source: source, _sourceTechId: sourceTechId || job.techId });
    try {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", job.id);
    } catch (_) {}
  };

  const onRowDragOver = (e, techId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverRow(techId);
  };

  const onRowDrop = (e, techId) => {
    e.preventDefault();
    setDragOverRow(null);
    if (!draggingJob) return;

    const rowEl = e.currentTarget;
    const rect = rowEl.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const { h, m } = offsetToTime(Math.max(0, x));

    const baseIso = localDateStr(date);
    const newStart = isoFromHM(baseIso, h, m);
    const jobBeingDragged = draggingJob;

    if (jobBeingDragged._source === "unassigned") {
      // Convert unassigned → first assignment. Legacy PATCH path so backend
      // seeds assignments[] with just this tech via legacy_mirror.
      const dur = jobBeingDragged.durationMin || 90;
      const startD = new Date(`${newStart}:00`);
      const endDate = new Date(startD.getTime() + dur * 60000);
      const endIso = `${localDateStr(endDate)}T${pad(endDate.getHours())}:${pad(endDate.getMinutes())}`;
      const techName = techs.find((t) => t.id === techId)?.name || "Tech";
      // Optimistic local: reload from server after the patch to pick up the
      // freshly-generated assignments[] array.
      jobsApi
        .patch(jobBeingDragged.id, { status: "Scheduled", start: newStart, end: endIso, techId, tech: techName })
        .then((updated) => {
          setJobs((prev) => [...prev.filter((j) => j.id !== updated.id), updated]);
          setUnassigned((prev) => prev.filter((u) => u.id !== jobBeingDragged.id));
        })
        .catch((err) => {
          console.error(err);
          toast.error("Failed to save drag");
        });
    } else {
      // Moving an existing tile. We move ONLY the source tech's assignment —
      // other techs on the same job keep their piece intact.
      const dur = durationMinutes(
        jobBeingDragged._srcStart || jobBeingDragged.start,
        jobBeingDragged._srcEnd || jobBeingDragged.end,
      );
      const startD = new Date(`${newStart}:00`);
      const endDate = new Date(startD.getTime() + dur * 60000);
      const endIso = `${localDateStr(endDate)}T${pad(endDate.getHours())}:${pad(endDate.getMinutes())}`;
      const sourceTechId = jobBeingDragged._sourceTechId || jobBeingDragged.techId;
      const assignments = jobBeingDragged.assignments || [];
      const isMultiTech = assignments.length > 1;

      if (isMultiTech && sourceTechId && sourceTechId !== techId) {
        // Multi-tech: transfer this piece from one tech to another (remove +
        // add). Preserve the other assignees. Legacy techId is auto-mirrored
        // to whichever assignment ends up first.
        const alreadyThere = assignments.some((a) => a.techId === techId);
        if (alreadyThere) {
          toast.error("That technician is already on this job at another time.");
          setDraggingJob(null);
          return;
        }
        (async () => {
          try {
            await jobsApi.removeAssignment(jobBeingDragged.id, sourceTechId);
            const updated = await jobsApi.addAssignment(jobBeingDragged.id, {
              techId, start: newStart, end: endIso,
            });
            setJobs((prev) => prev.map((j) => (j.id === updated.id ? updated : j)));
            toast.success(`Transferred piece to ${techs.find((t) => t.id === techId)?.name || "tech"}`);
          } catch (err) {
            console.error(err);
            toast.error("Failed to transfer piece");
          }
        })();
      } else if (isMultiTech && sourceTechId === techId) {
        // Same tech, different time slot — patch just this assignment.
        (async () => {
          try {
            const updated = await jobsApi.patchAssignment(jobBeingDragged.id, sourceTechId, {
              start: newStart, end: endIso,
            });
            setJobs((prev) => prev.map((j) => (j.id === updated.id ? updated : j)));
          } catch (err) {
            console.error(err);
            toast.error("Failed to move piece");
          }
        })();
      } else {
        // Solo job — legacy path (moves the whole thing).
        const techName = techs.find((t) => t.id === techId)?.name || jobBeingDragged.tech;
        setJobs((prev) =>
          prev.map((j) =>
            j.id === jobBeingDragged.id ? { ...j, techId, tech: techName, start: newStart, end: endIso } : j,
          ),
        );
        const prevStart = (jobBeingDragged.start || "").slice(0, 16);
        const wasScheduled =
          !!prevStart && !["Unassigned", "Completed", "Canceled"].includes(jobBeingDragged.status);
        jobsApi
          .patch(jobBeingDragged.id, { start: newStart, end: endIso, techId, tech: techName })
          .then((updated) => {
            setJobs((prev) => prev.map((j) => (j.id === updated.id ? updated : j)));
            if (wasScheduled && prevStart !== newStart && (jobBeingDragged.phone || jobBeingDragged.customerId)) {
              setRescheduleJob({ ...jobBeingDragged, start: newStart, end: endIso });
            }
          })
          .catch((err) => {
            console.error(err);
            toast.error("Failed to save drag");
          });
      }
    }
    setDraggingJob(null);
  };

  // Drop a tech-avatar chip onto a job tile → add them to the crew.
  const onAddTechToJob = async (job, techId) => {
    const already = (job.assignments || []).some((a) => a.techId === techId);
    if (already) {
      toast.info(`${techs.find((t) => t.id === techId)?.name || "That tech"} is already on this job.`);
      return;
    }
    try {
      const updated = await jobsApi.addAssignment(job.id, { techId });
      setJobs((prev) => prev.map((j) => (j.id === updated.id ? updated : j)));
      const t = techs.find((x) => x.id === techId);
      toast.success(`Added ${t?.name || "tech"} as a helper on ${job.id}`);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Could not add tech");
    }
  };

  // Drop on Unassigned panel — send back to queue
  const onUnassignedDrop = (e) => {
    e.preventDefault();
    if (!draggingJob || draggingJob._source !== "board") {
      setDraggingJob(null);
      return;
    }
    setJobs((prev) => prev.filter((j) => j.id !== draggingJob.id));
    setUnassigned((prev) => [
      {
        ...draggingJob,
        status: "Unassigned",
        durationMin: durationMinutes(draggingJob.start, draggingJob.end),
        techId: null,
        tech: "Unassigned",
        start: null,
        end: null,
      },
      ...prev,
    ]);
    jobsApi
      .patch(draggingJob.id, { status: "Unassigned", techId: null, tech: "Unassigned", start: null, end: null })
      .catch((err) => {
        console.error(err);
        toast.error("Failed to unassign");
      });
    setDraggingJob(null);
  };

  // Assign an unassigned job to a tech via dropdown. If the job already has a
  // scheduled start/end (set when the dispatcher saved it via the New Job
  // modal), keep that time. Otherwise pick the first free slot after that
  // tech's last existing job on the currently displayed calendar date,
  // clamped between 9:00 AM and 5:30 PM. Falls back to 9 AM.
  const assignUnassignedToTech = (job, techId) => {
    if (!techId) return;
    const tech = techs.find((t) => t.id === techId);
    if (!tech) {
      toast.error("Technician not found");
      return;
    }
    const dur = job.durationMin || 60;
    let startIso;
    let endIso;
    if (job.start) {
      // Preserve the date/time the user picked when creating the job.
      startIso = job.start.slice(0, 16);
      if (job.end) {
        endIso = job.end.slice(0, 16);
      } else {
        const startD = new Date(`${startIso}:00`);
        const endDate = new Date(startD.getTime() + dur * 60000);
        endIso = `${localDateStr(endDate)}T${pad(endDate.getHours())}:${pad(endDate.getMinutes())}`;
      }
    } else {
      const baseDate = localDateStr(date);
      const techJobsToday = jobs
        .filter((j) => j.techId === techId && (j.start || "").startsWith(baseDate))
        .map((j) => j.end || j.start)
        .filter(Boolean)
        .sort();
      const lastEnd = techJobsToday[techJobsToday.length - 1];
      if (lastEnd) {
        // Round to next 15-min for cleanliness
        const d = new Date(`${lastEnd}:00`);
        d.setMinutes(Math.ceil(d.getMinutes() / 15) * 15);
        const hh = d.getHours();
        if (hh < 9 || hh >= 18) {
          startIso = `${baseDate}T09:00`;
        } else {
          startIso = `${localDateStr(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
        }
      } else {
        startIso = `${baseDate}T09:00`;
      }
      const startD = new Date(`${startIso}:00`);
      const endDate = new Date(startD.getTime() + dur * 60000);
      endIso = `${localDateStr(endDate)}T${pad(endDate.getHours())}:${pad(endDate.getMinutes())}`;
    }

    const scheduled = {
      ...job,
      status: "Scheduled",
      start: startIso,
      end: endIso,
      techId,
      tech: tech.name,
    };
    // Optimistic update — remove from queue, add to calendar
    setUnassigned((prev) => prev.filter((u) => u.id !== job.id));
    setJobs((prev) => [...prev, scheduled]);
    jobsApi
      .patch(job.id, { status: "Scheduled", start: startIso, end: endIso, techId, tech: tech.name })
      .then(() => {
        toast.success(`Assigned ${job.id} → ${tech.name} at ${startIso.slice(11, 16)}`);
      })
      .catch((err) => {
        console.error(err);
        toast.error("Failed to assign — rolling back");
        // Roll back optimistic update
        setUnassigned((prev) => [job, ...prev]);
        setJobs((prev) => prev.filter((j) => j.id !== job.id));
      });
  };

  const changeDay = (delta) => {
    const d = new Date(date);
    if (view === "day") d.setDate(d.getDate() + delta);
    else if (view === "week") d.setDate(d.getDate() + delta * 7);
    else d.setMonth(d.getMonth() + delta);
    setDate(d);
  };

  const goToday = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    setDate(d);
  };

  const isToday = useMemo(() => {
    const t = new Date();
    return t.toDateString() === date.toDateString();
  }, [date]);

  const periodLabel = useMemo(() => {
    if (view === "day") return shortDate(date);
    if (view === "week") {
      const start = startOfWeek(date);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return `${shortDate(start)} \u2013 ${shortDate(end)}`;
    }
    return date.toLocaleDateString([], { month: "long", year: "numeric" });
  }, [date, view]);

  const nowOffset = useMemo(() => {
    if (!isToday) return null;
    const minutes = now.getHours() * 60 + now.getMinutes();
    const startMin = START_HOUR * 60;
    if (minutes < startMin || minutes > END_HOUR * 60) return null;
    return ((minutes - startMin) / 60) * HOUR_WIDTH;
  }, [now, isToday]);

  usePageHeader({
    title: "Dispatch Board",
    subtitle: `${periodLabel} \u2014 ${todayJobs.length} jobs · ${filteredTechs.length} technicians`,
  });

  return (
    <>
      <div className="p-6 pb-3 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-9" onClick={() => changeDay(-1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <button
            onClick={goToday}
            className="h-9 px-3 rounded-md bg-white border border-slate-200 hover:border-blue-300 text-[13px] font-semibold text-slate-800 flex items-center gap-1.5"
          >
            <CalendarIcon className="h-3.5 w-3.5 text-slate-500" />
            {isToday && view === "day" ? "Today" : periodLabel}
          </button>
          <Button variant="outline" size="sm" className="h-9" onClick={() => changeDay(1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>

          {/* View tabs */}
          <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-1 ml-2">
            {[
              { k: "day", label: "Day" },
              { k: "week", label: "Week" },
              { k: "month", label: "Month" },
              ...(hasModule("fleet_tracking") ? [{ k: "map", label: "Live Map" }] : []),
            ].map((v) => (
              <button
                key={v.k}
                onClick={() => setView(v.k)}
                data-testid={`dispatch-view-${v.k}`}
                className={`h-7 px-3 rounded-md text-[12px] font-medium transition-colors ${
                  v.k === "map"
                    ? "ml-2 bg-[#04d108] text-white shadow-sm hover:bg-[#04d108]"
                    : view === v.k ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {v.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg p-1">
            <Filter className="h-3.5 w-3.5 text-slate-400 ml-1" />
            {["All", "HVAC", "Plumbing", "Electrical"].map((f) => (
              <button
                key={f}
                onClick={() => setTradeFilter(f)}
                className={`h-7 px-2.5 rounded-md text-[11.5px] font-medium transition-colors ${
                  tradeFilter === f ? "bg-blue-50 text-blue-700" : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          <div className="hidden md:flex items-center gap-2 text-[11px] text-slate-500 px-2">
            <LegendDot color="#2563EB" /> HVAC
            <LegendDot color="#10B981" /> Plumbing
            <LegendDot color="#F59E0B" /> Electrical
            <LegendDot color="#EF4444" /> Gas
          </div>
        </div>
      </div>

      {view === "map" && hasModule("fleet_tracking") ? (
        <div className="flex-1 px-6 pb-6 min-h-0" data-testid="dispatch-map-view">
          <LiveFleetMap />
        </div>
      ) : (
      <div className="flex-1 px-6 pb-6 grid grid-cols-12 gap-4 min-h-0">
        {/* Main view (left/center) */}
        <div className="col-span-9 bg-white rounded-xl border border-slate-200 overflow-hidden flex flex-col min-h-0">
          {view === "day" && (
            <DayView
              filteredTechs={filteredTechs}
              todayJobs={todayJobs}
              dragOverRow={dragOverRow}
              onRowDragOver={onRowDragOver}
              onRowDrop={onRowDrop}
              setDragOverRow={setDragOverRow}
              setSelectedJob={setSelectedJob}
              onJobDragStart={onJobDragStart}
              setDraggingJob={setDraggingJob}
              onAddTechToJob={onAddTechToJob}
              nowOffset={nowOffset}
              scrollRef={scrollRef}
            />
          )}
          {view === "week" && (
            <WeekView date={date} jobs={jobs} filteredTechs={filteredTechs} setView={setView} setDate={setDate} setSelectedJob={setSelectedJob} />
          )}
          {view === "month" && (
            <MonthView date={date} jobs={jobs} setView={setView} setDate={setDate} />
          )}
        </div>

        {/* Unassigned queue (right) */}
        <div
          className="col-span-3 bg-white rounded-xl border border-slate-200 flex flex-col min-h-0 overflow-hidden"
          onDragOver={(e) => e.preventDefault()}
          onDrop={onUnassignedDrop}
        >
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Inbox className="h-4 w-4 text-slate-500" />
              <div>
                <div className="text-[14px] font-semibold text-slate-900">Unassigned</div>
                <div className="text-[11px] text-slate-500">{unassigned.length} jobs in queue</div>
              </div>
            </div>
            <button
              onClick={() => {
                setSelectedJob({
                  id: `J-${Math.floor(10500 + Math.random() * 1000)}`,
                  customer: "",
                  phone: "",
                  address: "",
                  city: "",
                  state: "AZ",
                  zip: "",
                  type: "HVAC Repair",
                  priority: "Normal",
                  status: "Unassigned",
                  durationMin: 90,
                  value: 0,
                  _unassigned: true,
                  _new: true,
                });
              }}
              title="Add new unassigned job"
              className="h-7 w-7 rounded-md hover:bg-blue-50 flex items-center justify-center text-slate-500 hover:text-blue-600 transition-colors"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {unassigned.length === 0 ? (
              <div className="text-center py-10 text-[12.5px] text-slate-400">
                Drop jobs here to unassign \u2014 all clear!
              </div>
            ) : (
              unassigned.map((u) => {
                const c = getJobColor(u.type);
                const pc = dispatchPriorityColors[u.priority] || dispatchPriorityColors.Normal;
                return (
                  <div
                    key={u.id}
                    onClick={() => setSelectedJob({ ...u, _unassigned: true })}
                    className="group cursor-pointer border-l-4 bg-white border border-slate-200 rounded-md p-2.5 hover:shadow-md hover:-translate-y-0.5 hover:border-blue-300 transition-all flex gap-1.5"
                    style={{ borderLeftColor: c.accent }}
                  >
                    <div
                      draggable
                      onDragStart={(e) => {
                        e.stopPropagation();
                        onJobDragStart(e, u, "unassigned");
                      }}
                      onDragEnd={() => setDraggingJob(null)}
                      onClick={(e) => e.stopPropagation()}
                      className="cursor-grab active:cursor-grabbing flex items-center justify-center -ml-1 -my-1 px-0.5 text-slate-300 hover:text-slate-500 group-hover:text-slate-400"
                      title="Drag to schedule"
                    >
                      <GripVertical className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-1">
                      <div className="min-w-0">
                        <div className="text-[12.5px] font-semibold text-slate-900 truncate" data-testid={`unassigned-card-title-${u.id}`}>{u.title || u.type || "Job"}</div>
                        <div className="text-[11.5px] text-slate-600 truncate">{u.customer}</div>
                        <div className="text-[10px] text-slate-400 truncate">{u.id}</div>
                        <div className="text-[11px] text-slate-500 truncate flex items-center gap-1 mt-0.5">
                          <MapPin className="h-2.5 w-2.5" />
                          {u.address}
                        </div>
                      </div>
                      <span
                        className="text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0"
                        style={{ background: pc.bg, color: pc.text }}
                      >
                        {u.priority}
                      </span>
                    </div>
                    <div className="mt-1.5">
                      <span
                        className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded"
                        style={{ background: c.bg, color: c.text }}
                      >
                        {u.qualification || u.type}
                      </span>
                    </div>
                    {u.start && (
                      <div
                        className="mt-1.5 flex items-center gap-1 text-[11px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded px-1.5 py-1"
                        data-testid={`unassigned-card-schedule-${u.id}`}
                      >
                        <CalendarIcon className="h-3 w-3 shrink-0" />
                        <span className="truncate">{formatScheduledLabel(u.start)}</span>
                      </div>
                    )}
                      <div
                        className="mt-1.5 flex items-center gap-1.5"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Users className="h-3 w-3 text-slate-400 shrink-0" />
                        <select
                          value=""
                          onChange={(e) => assignUnassignedToTech(u, e.target.value)}
                          data-testid={`unassigned-tech-select-${u.id}`}
                          className="flex-1 h-7 px-1.5 rounded-md border border-slate-200 bg-slate-50 hover:bg-white hover:border-blue-300 focus:border-blue-400 focus:bg-white text-[11px] font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/15 cursor-pointer transition-colors"
                          title="Assign to a technician"
                        >
                          <option value="" disabled>
                            Assign to tech…
                          </option>
                          {techs.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}{t.role ? ` · ${t.role}` : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="text-[10px] text-slate-400 mt-1 italic">Or drag to a tech row · Click to edit</div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
      )}

      {/* Job detail drawer */}
      {selectedJob && (
        <JobDetail
          job={selectedJob}
          techs={techs}
          onClose={() => setSelectedJob(null)}
          onOpenCustomer={(customerId) => setProfileCustomerId(customerId)}
          onSave={async (updates) => {
            // AssignTechControl bubbles up the fresh server doc — replace in
            // place so the timeline re-renders with the new assignments.
            if (updates && updates.__replaceWith) {
              const fresh = updates.__replaceWith;
              setJobs((prev) => prev.map((j) => (j.id === fresh.id ? fresh : j)));
              setUnassigned((prev) => {
                if (fresh.status === "Unassigned") {
                  const exists = prev.some((u) => u.id === fresh.id);
                  return exists ? prev.map((u) => (u.id === fresh.id ? fresh : u)) : [fresh, ...prev];
                }
                return prev.filter((u) => u.id !== fresh.id);
              });
              setSelectedJob(fresh.status === "Unassigned" ? { ...fresh, _unassigned: true } : fresh);
              return;
            }
            if (selectedJob._new) {
              // Add new job to unassigned queue (create via API)
              try {
                const created = await jobsApi.create({
                  customer: updates.customer || selectedJob.customer || "New Customer",
                  phone: updates.phone || selectedJob.phone || "",
                  address: updates.address || "",
                  city: updates.city || "Phoenix",
                  state: updates.state || "AZ",
                  zip: updates.zip || "",
                  type: updates.type || selectedJob.type || "HVAC Repair",
                  title: updates.type || selectedJob.type || "HVAC Repair",
                  priority: updates.priority || "Normal",
                  status: "Unassigned",
                  value: updates.value || 0,
                  description: updates.description || "",
                });
                setUnassigned((prev) => [created, ...prev]);
                setSelectedJob({ ...created, _unassigned: true });
                toast.success(`Job ${created.id} created`);
              } catch (err) {
                console.error(err);
                toast.error("Failed to create job");
              }
            } else if (selectedJob._unassigned) {
              const isNowAssigned = !!updates.techId && updates.status && updates.status !== "Unassigned";
              if (isNowAssigned) {
                // Job was in the Unassigned queue and now has a tech —
                // move it onto the timeline so it renders on that tech's row.
                const merged = { ...selectedJob, ...updates, _unassigned: false };
                setUnassigned((prev) => prev.filter((u) => u.id !== selectedJob.id));
                setJobs((prev) => {
                  const exists = prev.some((j) => j.id === selectedJob.id);
                  return exists ? prev.map((j) => (j.id === selectedJob.id ? merged : j)) : [...prev, merged];
                });
              } else {
                setUnassigned((prev) => prev.map((u) => (u.id === selectedJob.id ? { ...u, ...updates } : u)));
              }
              setSelectedJob((s) => (s ? { ...s, ...updates } : s));
              jobsApi
                .patch(selectedJob.id, updates)
                .then((fresh) => {
                  if (fresh && isNowAssigned) {
                    // Backend seeds assignments[] via legacy_mirror — replace
                    // the optimistic tile with the server truth.
                    setJobs((prev) => prev.map((j) => (j.id === fresh.id ? fresh : j)));
                  }
                })
                .catch((err) => {
                  console.error(err);
                  toast.error("Failed to save changes");
                });
            } else {
              const prevStart = (selectedJob.start || "").slice(0, 16);
              const nextStart = (updates.start || "").slice(0, 16);
              const wasScheduled =
                !!prevStart && !["Unassigned", "Completed", "Canceled"].includes(selectedJob.status);
              setJobs((prev) => prev.map((j) => (j.id === selectedJob.id ? { ...j, ...updates } : j)));
              setSelectedJob((s) => (s ? { ...s, ...updates } : s));
              jobsApi
                .patch(selectedJob.id, updates)
                .then(() => {
                  if (wasScheduled && nextStart && nextStart !== prevStart && (selectedJob.phone || selectedJob.customerId)) {
                    setRescheduleJob({ ...selectedJob, ...updates });
                  }
                })
                .catch((err) => {
                  console.error(err);
                  toast.error("Failed to save changes");
                });
            }
          }}
        />
      )}
      <CustomerProfileOverlay customerId={profileCustomerId} onClose={() => setProfileCustomerId(null)} />
      {rescheduleJob && <RescheduleTextPopup job={rescheduleJob} onClose={() => setRescheduleJob(null)} />}
    </>
  );
}

function JobBlock({ job, assignment, onClick, onDragStart, onDragEnd, onAddTech }) {
  // Prefer per-tech assignment times (staggered arrivals) with fallback to
  // the job-level start/end for legacy single-tech jobs.
  const start = (assignment && assignment.start) || job.start;
  const end = (assignment && assignment.end) || job.end;
  const left = timeToOffset(start);
  const width = (durationMinutes(start, end) / 60) * HOUR_WIDTH - 4;
  const c = getJobColor(job.type);
  const perStatus = (assignment && assignment.status) || null;
  const isDone = job.status === "Completed" || perStatus === "Done";
  const crew = (job.assignments || []).length;
  const extraCount = crew > 1 ? crew - 1 : 0;
  const [hover, setHover] = React.useState(false);
  const [dropHot, setDropHot] = React.useState(false);

  const handleDragOver = (e) => {
    // Only accept a tech-avatar drag (mimetype "application/x-tech-id"). Job
    // drags across rows are handled by the row-level drop.
    const types = e.dataTransfer?.types || [];
    if (Array.from(types).includes("application/x-tech-id")) {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = "copy";
      setDropHot(true);
    }
  };
  const handleDragLeave = () => setDropHot(false);
  const handleDrop = (e) => {
    const techId = e.dataTransfer.getData("application/x-tech-id");
    if (!techId) return;
    e.preventDefault();
    e.stopPropagation();
    setDropHot(false);
    onAddTech?.(job, techId);
  };

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      data-testid={`dispatch-job-tile-${job.id}${assignment ? `-${assignment.techId}` : ""}`}
      className={`absolute top-1.5 cursor-grab active:cursor-grabbing rounded-md border border-slate-200 border-l-4 bg-white px-2 py-1 hover:shadow-md hover:-translate-y-px transition-all overflow-hidden z-10 ${
        dropHot ? "ring-2 ring-blue-500 ring-offset-1" : ""
      }`}
      style={{
        left,
        width: Math.max(width, 60),
        height: ROW_HEIGHT - 14,
        borderLeftColor: c.accent,
        opacity: isDone ? 0.7 : 1,
      }}
    >
      <div className="flex items-center justify-between gap-1">
        <div className="text-[11px] font-bold truncate" style={{ color: c.accent }}>
          {job.id} · {job.type}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {extraCount > 0 && (
            <span
              data-testid={`dispatch-crew-badge-${job.id}`}
              title={`${crew} techs on this job`}
              className="text-[9.5px] font-bold px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 border border-indigo-200 flex items-center gap-0.5"
            >
              <Users className="h-2.5 w-2.5" />
              +{extraCount}
            </span>
          )}
          {job.priority === "High" && <AlertCircle className="h-3 w-3 text-red-500 shrink-0" />}
        </div>
      </div>
      <div className="text-[11.5px] font-semibold text-slate-900 truncate">{job.customer}</div>
      <div className="text-[10.5px] text-slate-600 truncate flex items-center gap-1">
        <MapPin className="h-2.5 w-2.5" />
        {job.address}{job.city ? `, ${job.city}` : ""}{job.state ? `, ${job.state}` : ""}
      </div>
      <div className="mt-0.5 flex items-center justify-between gap-1 text-[10px]">
        <span className="text-slate-500 flex items-center gap-0.5">
          <Clock className="h-2.5 w-2.5" />
          {new Date(start).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
          {perStatus && perStatus !== "Assigned" && (
            <span className="ml-1 px-1 rounded bg-slate-100 text-slate-700 font-semibold">{perStatus}</span>
          )}
        </span>
        <span className="font-bold text-emerald-700">${(job.value || 0).toLocaleString()}</span>
      </div>
      {hover && extraCount > 0 && (
        <div
          className="absolute inset-x-1 -bottom-0.5 h-1 rounded-full bg-indigo-300/60 pointer-events-none"
          title="Multi-tech job"
        />
      )}
    </div>
  );
}

function JobDetail({ job, techs = [], onClose, onSave, onOpenCustomer }) {
  const isUnassigned = !!job._unassigned;
  const isNew = !!job._new;
  const [draft, setDraft] = useState({
    customer: job.customer || "",
    phone: job.phone || "",
    type: job.type || "HVAC Repair",
    value: job.value || 0,
    address: job.address || "",
    city: job.city || "",
    state: job.state || "",
    zip: job.zip || "",
    start: job.start ? job.start.slice(0, 16) : "",
    end: job.end ? job.end.slice(0, 16) : "",
    status: job.status || "Scheduled",
    priority: job.priority || "Normal",
    durationMin: job.durationMin || 90,
    techId: job.techId || "",
    tech: job.tech || "Unassigned",
  });
  const c = getJobColor(draft.type || job.type);
  const [dirty, setDirty] = useState(false);
  const navigate = useNavigate();

  // Reset draft when a different job opens
  useEffect(() => {
    setDraft({
      customer: job.customer || "",
      phone: job.phone || "",
      type: job.type || "HVAC Repair",
      value: job.value || 0,
      address: job.address || "",
      city: job.city || "",
      state: job.state || "",
      zip: job.zip || "",
      start: job.start ? job.start.slice(0, 16) : "",
      end: job.end ? job.end.slice(0, 16) : "",
      status: job.status || "Scheduled",
      priority: job.priority || "Normal",
      durationMin: job.durationMin || 90,
      techId: job.techId || "",
      tech: job.tech || "Unassigned",
    });
    setDirty(isNew);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.id]);

  const update = (patch) => {
    setDraft((d) => ({ ...d, ...patch }));
    setDirty(true);
  };

  const handleSave = () => {
    onSave(draft);
    setDirty(false);
    // Close the drawer after saving (matches expected UX — click Save → drawer
    // closes and the job appears on the tech's calendar row).
    onClose();
  };

  const handleCall = () => {
    if (job.phone) window.location.href = `tel:${job.phone.replace(/[^0-9+]/g, "")}`;
  };
  const handleMap = () => {
    const q = encodeURIComponent(`${draft.address}, ${draft.city}, ${draft.state} ${draft.zip}`);
    window.open(`https://www.google.com/maps/search/?api=1&query=${q}`, "_blank", "noopener");
  };
  const handleViewJob = () => {
    if (!job.id) return;
    onClose();
    navigate(`/jobs?id=${encodeURIComponent(job.id)}`);
  };

  const STATUS_OPTIONS = ["Unassigned", "Scheduled", "Dispatched", "En Route", "On Site", "In Progress", "Completed", "Canceled"];
  const PRIORITY_OPTIONS = ["High", "Normal", "Low"];

  // Bold, color-coded status pill shown in the header (upper right).
  // If a tech is now assigned, show the real status (Scheduled/etc.) — even
  // for a job that was opened from the "Unassigned" queue.
  const effectiveStatus = (isUnassigned && !draft.techId && draft.status !== "Completed" && draft.status !== "Canceled")
    ? "Unassigned"
    : draft.status || "Scheduled";
  const statusStyle = getStatusStyle(effectiveStatus);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-slate-900/40" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-[460px] h-full bg-white shadow-2xl border-l border-slate-200 overflow-y-auto flex flex-col"
      >
        <div
          className="px-5 py-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10"
          style={{ borderTop: `4px solid ${c.accent}` }}
        >
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{isNew ? "New Job" : "Job"}</div>
            <div className="text-[16px] font-bold text-slate-900">{job.id}</div>
          </div>
          <div className="flex items-center gap-2">
            <span
              data-testid="dispatch-job-status-pill"
              className={`inline-flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wide px-2.5 py-1 rounded-full ring-1 ring-inset ${statusStyle.bg} ${statusStyle.text} ${statusStyle.ring}`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${statusStyle.dot}`} />
              {effectiveStatus}
            </span>
            <button onClick={onClose} className="h-8 w-8 rounded-md hover:bg-slate-100 flex items-center justify-center">
              <X className="h-4 w-4 text-slate-500" />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-4 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {isNew ? (
              <select
                value={draft.type}
                onChange={(e) => update({ type: e.target.value })}
                className="text-[12px] font-bold rounded-full border px-2 py-0.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                style={{ background: c.bg, color: c.text, borderColor: c.border }}
              >
                {["HVAC Repair", "HVAC Install", "HVAC Tune-up", "Plumbing Service", "Drain Cleaning", "Water Heater", "Electrical Inspection", "Panel Upgrade", "Gas Service"].map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            ) : (
              <span
                className="inline-flex items-center text-[11px] font-bold px-2 py-0.5 rounded-full"
                style={{ background: c.bg, color: c.text }}
              >
                {job.type}
              </span>
            )}
          </div>

          {/* Customer (editable when new) */}
          {isNew ? (
            <div className="space-y-1.5">
              <Field label="Customer Name" icon={Briefcase}>
                <input
                  value={draft.customer}
                  onChange={(e) => update({ customer: e.target.value })}
                  placeholder="e.g., Jane Doe or Acme Corp"
                  className={inputClass}
                  autoFocus
                />
              </Field>
              <Field label="Phone Number" icon={Phone}>
                <input
                  value={draft.phone}
                  onChange={(e) => update({ phone: e.target.value })}
                  placeholder="(555) 123-4567"
                  className={inputClass}
                />
              </Field>
            </div>
          ) : (
            <div>
              {onOpenCustomer && job.customerId && !job._new && !job._unassigned ? (
                <button
                  type="button"
                  onClick={() => onOpenCustomer(job.customerId)}
                  data-testid="dispatch-customer-link"
                  className="text-[18px] font-semibold text-blue-600 hover:underline text-left"
                  title={`Open ${job.customer}'s profile`}
                >
                  {job.customer}
                </button>
              ) : (
                <div className="text-[18px] font-semibold text-slate-900">{job.customer}</div>
              )}
              <div className="mt-1 flex items-center gap-2 text-[13px] text-slate-700">
                <Phone className="h-3.5 w-3.5 text-slate-400" />
                {job.phone ? (
                  <a href={`tel:${job.phone.replace(/[^0-9+]/g, "")}`} className="text-blue-600 hover:underline">
                    {job.phone}
                  </a>
                ) : (
                  <span className="text-slate-400 italic">no phone on file</span>
                )}
              </div>
            </div>
          )}

          {/* Editable Address */}
          <Field label="Service Address" icon={MapPin}>
            <input
              value={draft.address}
              onChange={(e) => update({ address: e.target.value })}
              placeholder="Street address"
              className={inputClass}
            />
            <div className="grid grid-cols-6 gap-1.5 mt-1.5">
              <input
                value={draft.city}
                onChange={(e) => update({ city: e.target.value })}
                placeholder="City"
                className={`${inputClass} col-span-3`}
              />
              <input
                value={draft.state}
                onChange={(e) => update({ state: e.target.value.toUpperCase().slice(0, 2) })}
                placeholder="ST"
                maxLength={2}
                className={`${inputClass} col-span-1`}
              />
              <input
                value={draft.zip}
                onChange={(e) => update({ zip: e.target.value })}
                placeholder="ZIP"
                maxLength={10}
                className={`${inputClass} col-span-2`}
              />
            </div>
            {(draft.address || draft.city) && (
              <div className="mt-1 text-[11px] text-slate-500">
                {[draft.address, [draft.city, draft.state].filter(Boolean).join(", "), draft.zip].filter(Boolean).join(" \u00b7 ")}
              </div>
            )}
          </Field>

          {/* Editable Schedule + Duration (Duration shown only for unassigned jobs) */}
          {isUnassigned && (
            <Field label="Estimated Duration" icon={Clock}>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={15}
                  step={15}
                  value={draft.durationMin}
                  onChange={(e) => update({ durationMin: parseInt(e.target.value, 10) || 0 })}
                  className={`${inputClass} w-28`}
                  data-testid="dispatch-duration-input"
                />
                <span className="text-[12px] text-slate-500">minutes</span>
              </div>
              <div className="mt-1.5 text-[11px] text-slate-500">
                Drag this job onto a tech row in the timeline to schedule it, or set a start/end time below.
              </div>
            </Field>
          )}
          <Field label="Scheduled Time" icon={Clock}>
            <div className="grid grid-cols-2 gap-1.5">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-0.5">Start</div>
                <input
                  type="datetime-local"
                  value={draft.start}
                  onChange={(e) => update({ start: e.target.value })}
                  className={inputClass}
                  data-testid="dispatch-start-input"
                />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-0.5">End</div>
                <input
                  type="datetime-local"
                  value={draft.end}
                  onChange={(e) => update({ end: e.target.value })}
                  className={inputClass}
                  data-testid="dispatch-end-input"
                />
              </div>
            </div>
          </Field>

          {/* Multi-tech assignments — checkbox multi-select + qualification/
              schedule filtering (Assign Technician control). For a brand-new
              (unsaved) draft we fall back to the legacy single-tech dropdown
              since it needs a persisted job id to call the assignments API. */}
          {!isNew && job.id && (
            <Field label="Assigned technicians" icon={Users}>
              <AssignTechControl
                job={job}
                techs={techs}
                canAssign
                size="md"
                onJobUpdated={(updated) => onSave?.({ __replaceWith: updated })}
              />
              <div className="mt-1.5 text-[11px] text-slate-500">
                Greyed-out technicians aren&apos;t qualified or scheduled for this job — tap one to override.
              </div>
            </Field>
          )}
          {isNew && (
            <Field label="Assign to tech" icon={Users}>
              <select
                value={draft.techId || ""}
                onChange={(e) => {
                  const id = e.target.value;
                  const t = techs.find((x) => x.id === id);
                  const patch = { techId: id || "", tech: t ? t.name : "Unassigned" };
                  if (id && (draft.status === "Unassigned" || !draft.status)) {
                    patch.status = "Scheduled";
                  } else if (!id && draft.status !== "Completed" && draft.status !== "Canceled") {
                    patch.status = "Unassigned";
                  }
                  update(patch);
                }}
                data-testid="dispatch-assign-tech"
                className={`${inputClass} cursor-pointer`}
              >
                <option value="">Unassigned</option>
                {techs.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}{t.role ? ` \u2014 ${t.role}` : ""}
                  </option>
                ))}
              </select>
              <div className="mt-1.5 text-[11px] text-slate-500">
                You can add helpers after saving.
              </div>
            </Field>
          )}

          {/* Editable Status + Priority */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Status" icon={Briefcase}>
              <select
                value={draft.status}
                onChange={(e) => update({ status: e.target.value })}
                className={`${inputClass} cursor-pointer`}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </Field>
            <Field label="Priority" icon={AlertCircle}>
              <select
                value={draft.priority}
                onChange={(e) => update({ priority: e.target.value })}
                className={`${inputClass} cursor-pointer`}
              >
                {PRIORITY_OPTIONS.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Estimated Value" icon={DollarSign}>
            {isNew ? (
              <div className="flex items-center gap-1">
                <span className="text-[13px] text-slate-500">$</span>
                <input
                  type="number"
                  min={0}
                  value={draft.value}
                  onChange={(e) => update({ value: parseFloat(e.target.value) || 0 })}
                  className={inputClass}
                />
              </div>
            ) : (
              <span className="font-bold text-emerald-700 text-[15px]">${(draft.value ?? job.value).toLocaleString()}</span>
            )}
          </Field>

          <div className="grid grid-cols-2 gap-2 pt-3 border-t border-slate-100">
            <Button variant="outline" className="h-9 text-[12.5px]" onClick={handleCall}>
              <Phone className="h-3.5 w-3.5 mr-1.5" /> Call Customer
            </Button>
            <Button variant="outline" className="h-9 text-[12.5px]" onClick={handleMap}>
              <MapPin className="h-3.5 w-3.5 mr-1.5" /> View on Map
            </Button>
          </div>

          {!isNew && job.id && (
            <div className="flex justify-center pt-3">
              <Button onClick={handleViewJob} data-testid="dispatch-view-job" className="h-9 px-8 text-[12.5px] bg-blue-600 hover:bg-blue-700 text-white">
                <Briefcase className="h-3.5 w-3.5 mr-1.5" /> View Job
              </Button>
            </div>
          )}
        </div>

        {/* Sticky save footer */}
        <div className="border-t border-slate-200 px-5 py-3 bg-white sticky bottom-0 flex items-center gap-2">
          <Button
            onClick={handleSave}
            disabled={!dirty || (isNew && !draft.customer.trim())}
            className="flex-1 h-10 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-500 text-white text-[13px] font-medium"
          >
            {isNew ? (draft.customer.trim() ? "Create Job" : "Enter Customer Name") : dirty ? "Save Changes" : "No Changes"}
          </Button>
          <Button variant="outline" onClick={onClose} className="h-10 text-[13px]">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

const inputClass =
  "w-full h-9 px-2.5 rounded-md border border-slate-200 bg-white text-[13px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-colors";

function Row({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-2.5 text-[13px]">
      <Icon className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
      <div className="flex-1">
        <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
        <div className="text-slate-700">{children}</div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, children }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1.5">
        {Icon && <Icon className="h-3.5 w-3.5 text-slate-400" />}
        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
      </div>
      {children}
    </div>
  );
}

function LegendDot({ color }) {
  return <span className="h-2 w-2 rounded-full" style={{ background: color }} />;
}

// Bold, color-coded pill styling for job status shown in the JobDetail header.
// Colors were chosen so red = attention needed, blue = closed-out, lime = actively happening.
function getStatusStyle(status) {
  const map = {
    Unassigned:    { bg: "bg-red-100",     text: "text-red-700",     ring: "ring-red-300",     dot: "bg-red-500" },
    Scheduled:     { bg: "bg-emerald-100", text: "text-emerald-700", ring: "ring-emerald-300", dot: "bg-emerald-500" },
    Dispatched:    { bg: "bg-amber-100",   text: "text-amber-800",   ring: "ring-amber-300",   dot: "bg-amber-500" },
    "In Progress": { bg: "bg-lime-100",    text: "text-lime-800",    ring: "ring-lime-300",    dot: "bg-lime-500" },
    Completed:     { bg: "bg-blue-100",    text: "text-blue-700",    ring: "ring-blue-300",    dot: "bg-blue-500" },
    Canceled:      { bg: "bg-slate-200",   text: "text-slate-600",   ring: "ring-slate-300",   dot: "bg-slate-400" },
  };
  return map[status] || map.Scheduled;
}

// ---------- DAY VIEW ----------
function DayView({
  filteredTechs,
  todayJobs,
  dragOverRow,
  onRowDragOver,
  onRowDrop,
  setDragOverRow,
  setSelectedJob,
  onJobDragStart,
  setDraggingJob,
  onAddTechToJob,
  nowOffset,
  scrollRef,
}) {
  return (
    <>
      {/* Hour header */}
      <div className="flex border-b border-slate-200 sticky top-0 bg-white z-20">
        <div className="w-44 shrink-0 border-r border-slate-200 px-3 py-2 text-[11px] uppercase tracking-wider font-semibold text-slate-500 bg-slate-50/60">
          Technician
        </div>
        <div ref={scrollRef} className="flex-1 overflow-x-auto">
          <div className="flex" style={{ width: HOURS.length * HOUR_WIDTH }}>
            {HOURS.map((h) => (
              <div
                key={h}
                className="border-r border-slate-100 text-[11px] font-semibold text-slate-500 px-2 py-2"
                style={{ width: HOUR_WIDTH }}
              >
                {fmtHour(h)}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Rows */}
      <div className="flex-1 overflow-y-auto">
        <div className="flex">
          <div className="w-44 shrink-0 border-r border-slate-200 bg-slate-50/40">
            {filteredTechs.map((t) => (
              <div
                key={t.id}
                className="border-b border-slate-100 px-3 flex items-center gap-2.5"
                style={{ height: ROW_HEIGHT }}
              >
                <div
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = "copy";
                    e.dataTransfer.setData("application/x-tech-id", t.id);
                    e.dataTransfer.setData("text/plain", t.id);
                  }}
                  data-testid={`dispatch-tech-avatar-${t.id}`}
                  title={`Drag ${t.name} onto a job tile to add them as a helper`}
                  className="h-9 w-9 rounded-full text-white text-[12px] font-semibold flex items-center justify-center shrink-0 cursor-grab active:cursor-grabbing"
                  style={{ background: t.color }}
                >
                  {t.avatar}
                </div>
                <div className="min-w-0">
                  <div className="text-[12.5px] font-semibold text-slate-900 truncate">{t.name}</div>
                  <div className="text-[10.5px] text-slate-500 truncate">{t.role}</div>
                  <div className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-medium text-slate-600">
                    <span
                      className="h-1.5 w-1.5 rounded-full"
                      style={{
                        background:
                          t.status === "On Job"
                            ? "#10B981"
                            : t.status === "Available"
                              ? "#3B82F6"
                              : t.status === "Lunch"
                                ? "#EF4444"
                                : "#F59E0B",
                      }}
                    />
                    {t.status}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div
            className="flex-1 overflow-x-auto"
            onScroll={(e) => {
              if (scrollRef.current) scrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
            }}
          >
            <div className="relative" style={{ width: HOURS.length * HOUR_WIDTH }}>
              {nowOffset !== null && (
                <div
                  className="absolute top-0 bottom-0 z-30 pointer-events-none"
                  style={{ left: nowOffset, width: 2, background: "#EF4444" }}
                >
                  <div className="absolute -top-0.5 -left-1.5 w-3 h-3 rounded-full bg-red-500 ring-2 ring-white" />
                </div>
              )}

              {filteredTechs.map((t) => {
                // Show a tile for every assignment on this tech's row —
                // multi-tech jobs appear on each assignee's timeline with
                // their own start/end (staggered arrivals).
                const techTiles = [];
                for (const j of todayJobs) {
                  const assignments = j.assignments || [];
                  if (assignments.length > 0) {
                    for (const a of assignments) {
                      if (a.techId === t.id) techTiles.push({ job: j, assignment: a });
                    }
                  } else if (j.techId === t.id) {
                    // Legacy single-tech job with no assignments[] array yet.
                    techTiles.push({ job: j, assignment: null });
                  }
                }
                const isOver = dragOverRow === t.id;
                return (
                  <div
                    key={t.id}
                    onDragOver={(e) => onRowDragOver(e, t.id)}
                    onDragLeave={() => setDragOverRow(null)}
                    onDrop={(e) => onRowDrop(e, t.id)}
                    className={`relative border-b border-slate-100 transition-colors ${
                      isOver ? "bg-blue-50/60" : "bg-white"
                    }`}
                    style={{ height: ROW_HEIGHT }}
                  >
                    {HOURS.map((h, i) => (
                      <div
                        key={h}
                        className="absolute top-0 bottom-0 border-r border-slate-100"
                        style={{ left: i * HOUR_WIDTH, width: HOUR_WIDTH }}
                      />
                    ))}
                    {HOURS.map((h, i) => (
                      <div
                        key={`half-${h}`}
                        className="absolute top-0 bottom-0 border-r border-dashed border-slate-50"
                        style={{ left: i * HOUR_WIDTH + HOUR_WIDTH / 2, width: 1 }}
                      />
                    ))}
                    {techTiles.map(({ job: j, assignment: a }) => (
                      <JobBlock
                        key={`${j.id}-${a ? a.techId : "solo"}`}
                        job={j}
                        assignment={a}
                        onClick={() => setSelectedJob(j)}
                        onDragStart={(e) => onJobDragStart(e, j, "board", a?.techId || t.id)}
                        onDragEnd={() => setDraggingJob(null)}
                        onAddTech={onAddTechToJob}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ---------- WEEK VIEW ----------
function WeekView({ date, jobs, filteredTechs, setView, setDate, setSelectedJob }) {
  const start = startOfWeek(date);
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    return d;
  });

  return (
    <div className="flex-1 overflow-auto">
      <table className="w-full border-separate border-spacing-0">
        <thead className="sticky top-0 bg-white z-10">
          <tr>
            <th className="w-44 text-left text-[11px] uppercase tracking-wider font-semibold text-slate-500 px-3 py-2 border-b border-slate-200 bg-slate-50/60">
              Technician
            </th>
            {days.map((d) => {
              const isCurrent = d.toDateString() === new Date().toDateString();
              return (
                <th
                  key={d.toISOString()}
                  className={`text-center text-[11px] font-semibold px-2 py-2 border-b border-slate-200 ${
                    isCurrent ? "bg-blue-50 text-blue-700" : "bg-white text-slate-600"
                  }`}
                >
                  <button
                    className="hover:text-blue-700 transition-colors"
                    onClick={() => {
                      setDate(d);
                      setView("day");
                    }}
                  >
                    <div className="uppercase tracking-wider text-[10px] text-slate-500">
                      {d.toLocaleDateString([], { weekday: "short" })}
                    </div>
                    <div className={`mt-0.5 text-[15px] font-bold ${isCurrent ? "text-blue-700" : "text-slate-800"}`}>
                      {d.getDate()}
                    </div>
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {filteredTechs.map((t) => (
            <tr key={t.id}>
              <td className="border-b border-slate-100 px-3 py-2 align-top bg-slate-50/40">
                <div className="flex items-center gap-2">
                  <div
                    className="h-8 w-8 rounded-full text-white text-[11px] font-semibold flex items-center justify-center shrink-0"
                    style={{ background: t.color }}
                  >
                    {t.avatar}
                  </div>
                  <div className="min-w-0">
                    <div className="text-[12px] font-semibold text-slate-900 truncate">{t.name}</div>
                    <div className="text-[10px] text-slate-500 truncate">{t.role}</div>
                  </div>
                </div>
              </td>
              {days.map((d) => {
                const dayStr = localDateStr(d);
                const dayJobs = jobs.filter((j) => j.techId === t.id && j.start.startsWith(dayStr));
                const total = dayJobs.reduce((sum, j) => sum + (j.value || 0), 0);
                return (
                  <td key={d.toISOString()} className="border-b border-l border-slate-100 align-top p-1.5 min-w-[120px]">
                    {dayJobs.length === 0 ? (
                      <div className="text-[10.5px] text-slate-300 italic text-center py-3">—</div>
                    ) : (
                      <div className="space-y-1">
                        {dayJobs.slice(0, 3).map((j) => {
                          const c = getJobColor(j.type);
                          return (
                            <div
                              key={j.id}
                              onClick={() => setSelectedJob(j)}
                              className="cursor-pointer rounded border border-slate-200 border-l-4 bg-white px-1.5 py-1 hover:shadow-sm transition-all"
                              style={{ borderLeftColor: c.accent }}
                            >
                              <div className="text-[10px] font-bold" style={{ color: c.accent }}>
                                {new Date(j.start).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                              </div>
                              <div className="text-[10.5px] text-slate-800 font-semibold truncate">{j.customer}</div>
                              <div className="text-[10px] text-slate-500 truncate">{j.type}</div>
                            </div>
                          );
                        })}
                        {dayJobs.length > 3 && (
                          <div className="text-[10px] text-slate-500 italic px-1">+{dayJobs.length - 3} more</div>
                        )}
                        <div className="text-[10px] font-semibold text-emerald-700 px-1 pt-0.5">${total.toLocaleString()}</div>
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------- MONTH VIEW ----------
function MonthView({ date, jobs, setView, setDate }) {
  const monthStart = startOfMonth(date);
  const gridStart = startOfWeek(monthStart);
  const cells = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(gridStart);
    d.setDate(d.getDate() + i);
    return d;
  });
  const monthIdx = monthStart.getMonth();
  const today = new Date();

  return (
    <div className="flex-1 overflow-auto">
      <div className="grid grid-cols-7 border-b border-slate-200 sticky top-0 bg-white z-10">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500 text-center bg-slate-50/60 border-r border-slate-200 last:border-r-0">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 auto-rows-fr" style={{ minHeight: "calc(100% - 40px)" }}>
        {cells.map((d) => {
          const inMonth = d.getMonth() === monthIdx;
          const isToday = d.toDateString() === today.toDateString();
          const dayStr = localDateStr(d);
          const dayJobs = jobs.filter((j) => j.start.startsWith(dayStr));
          const trades = {};
          dayJobs.forEach((j) => {
            const c = getJobColor(j.type);
            trades[c.accent] = (trades[c.accent] || 0) + 1;
          });
          const total = dayJobs.reduce((sum, j) => sum + (j.value || 0), 0);
          return (
            <button
              key={d.toISOString()}
              onClick={() => {
                setDate(new Date(d));
                setView("day");
              }}
              className={`text-left p-2 border-r border-b border-slate-100 last:border-r-0 hover:bg-blue-50/30 transition-colors min-h-[100px] ${
                inMonth ? "bg-white" : "bg-slate-50/30"
              }`}
            >
              <div className="flex items-center justify-between">
                <span
                  className={`text-[12.5px] font-semibold ${
                    isToday
                      ? "h-6 w-6 rounded-full bg-blue-600 text-white flex items-center justify-center"
                      : inMonth
                        ? "text-slate-900"
                        : "text-slate-400"
                  }`}
                >
                  {d.getDate()}
                </span>
                {dayJobs.length > 0 && (
                  <span className="text-[10px] font-semibold text-slate-500">{dayJobs.length} jobs</span>
                )}
              </div>
              {dayJobs.length > 0 && (
                <>
                  <div className="mt-1.5 flex flex-wrap gap-0.5">
                    {Object.entries(trades).map(([color, count]) => (
                      <span
                        key={color}
                        className="inline-flex items-center gap-0.5 text-[9.5px] font-semibold px-1 py-0.5 rounded"
                        style={{ background: `${color}1f`, color }}
                      >
                        <span className="h-1.5 w-1.5 rounded-full" style={{ background: color }} />
                        {count}
                      </span>
                    ))}
                  </div>
                  <div className="mt-1 text-[10.5px] font-bold text-emerald-700">${total.toLocaleString()}</div>
                </>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
